/**
 * PaymentKanban - Kanban board for payment management
 *
 * 4 columns:
 * 1. צפי הכנסה עתידי (expected) - future appointments
 * 2. ממתין לתשלום (pending)
 * 3. שולם (paid)
 * 4. הופקה חשבונית (invoice_manual/invoice_auto)
 *
 * Features:
 * - Drag and drop between columns
 * - Smart invoice handling (check Morning integration)
 * - Click to view payment details
 * - Visual indicators for split, refunded records
 *
 * Error Codes: CK-8840 - CK-8849
 */

import { useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
// Grow payment links (grow_integration.md Phase 5): chip + request button on cards, manual-paid guard on drop
import useGrowLinksMap from "../grow/useGrowLinksMap";
import GrowLinkStatusChip from "../grow/GrowLinkStatusChip";
import RequestPaymentButton from "../grow/RequestPaymentButton";
import useCancelGrowLinkOnManualPayment from "../../hooks/useCancelGrowLinkOnManualPayment";
import { GROW_ACTIVE_LINK_STATUSES } from "../../firebase";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
  selectCanCreateGreenInvoice,
} from "../../store/slices/authSlice";
import {
  useGetTransactionsForKanbanQuery,
  useUpdateTransactionStatusMutation,
  useForgiveDebtMutation,
} from "../../store/services/financeApi";
import { calendarApi } from "../../store/services/calendarApi";
import { TRANSACTION_STATUS } from "../../firebase";
import DatePicker from "../ui/DatePicker";
import {
  Clock,
  CheckCircle,
  Receipt,
  CalendarBlank,
  ArrowsClockwise,
  Hourglass,
  Info,
  X,
  Warning,
  CalendarCheck,
} from "@phosphor-icons/react";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { useToast } from "../../contexts/ToastContext";
import "./PaymentKanban.css";

// Column configuration
const KANBAN_COLUMNS = [
  {
    id: "expected",
    title: "צפי הכנסה עתידי",
    icon: Hourglass,
    color: "blue",
    acceptsStatuses: [TRANSACTION_STATUS.EXPECTED],
    targetStatus: TRANSACTION_STATUS.EXPECTED,
    infoTooltip: "פגישות שעוד לא התקיימו ועלולות להתבטל. ניתן לגרור ידנית לעמודה אחרת לעדכון הסטטוס.",
  },
  {
    id: "pending",
    title: "ממתין לתשלום",
    icon: Clock,
    color: "warning",
    acceptsStatuses: [TRANSACTION_STATUS.PENDING, "pending_collection"],
    targetStatus: TRANSACTION_STATUS.PENDING,
  },
  {
    id: "paid",
    title: "שולם",
    icon: CheckCircle,
    color: "primary", // System primary color (teal)
    acceptsStatuses: [TRANSACTION_STATUS.PAID],
    targetStatus: TRANSACTION_STATUS.PAID,
  },
  {
    id: "invoiced",
    title: "הופקה חשבונית",
    icon: Receipt,
    color: "purple", // Blue/purple for invoices
    acceptsStatuses: [TRANSACTION_STATUS.INVOICE_MANUAL, TRANSACTION_STATUS.INVOICE_AUTO],
    targetStatus: null, // Determined by Morning integration
  },
];

function PaymentKanban({ onCardClick, onInvoiceRequest, includeAllTherapists = false }) {
  const dispatch = useDispatch();
  const { showError } = useToast();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);

  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);

  // Fetch kanban data
  const {
    data: kanbanData,
    isLoading,
  } = useGetTransactionsForKanbanQuery(
    { clinicId, therapistId, includeAllTherapists },
    { skip: !clinicId || !therapistId, pollingInterval: 30000 }
  );

  // Grow: links for every visible card (one query), shared popover, manual-paid guard
  const allKanbanItems = useMemo(
    () => [
      ...(kanbanData?.expected || []),
      ...(kanbanData?.pending || []),
      ...(kanbanData?.paid || []),
      ...(kanbanData?.invoiced || []),
    ],
    [kanbanData]
  );
  const growLinks = useGrowLinksMap(allKanbanItems);
  const growGuard = useCancelGrowLinkOnManualPayment();

  // Mutation for status updates
  const [updateTransactionStatus, { isLoading: isUpdating }] =
    useUpdateTransactionStatusMutation();

  // Mutation for cancelling expected forecast
  const [forgiveDebt] = useForgiveDebtMutation();

  // Drag state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Invoice modal state
  const [showInvoiceChoice, setShowInvoiceChoice] = useState(false);
  const [pendingInvoiceItem, setPendingInvoiceItem] = useState(null);
  // Manual invoice entry (optional number + date)
  const [showManualInvoiceForm, setShowManualInvoiceForm] = useState(false);
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState("");
  const [manualInvoiceDate, setManualInvoiceDate] = useState("");
  
  // Tooltip state
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Cancel expected forecast state
  const [showCancelForecast, setShowCancelForecast] = useState(false);
  const [pendingCancelItem, setPendingCancelItem] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Get items for each column
  const getColumnItems = useCallback(
    (columnId) => {
      if (!kanbanData) return [];

      switch (columnId) {
        case "expected":
          return kanbanData.expected || [];
        case "pending":
          return kanbanData.pending || [];
        case "paid":
          return kanbanData.paid || [];
        case "invoiced":
          return kanbanData.invoiced || [];
        default:
          return [];
      }
    },
    [kanbanData]
  );

  // Drag handlers
  const handleDragStart = (e, item, sourceColumn) => {
    setDraggedItem({ item, sourceColumn });
    e.dataTransfer.effectAllowed = "move";
    setTimeout(() => {
      e.target.classList.add("dragging");
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove("dragging");
    setDraggedItem(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedItem || draggedItem.sourceColumn === targetColumn.id) {
      return;
    }

    const { item } = draggedItem;

    // Handle invoice column specially
    if (targetColumn.id === "invoiced") {
      setPendingInvoiceItem(item);
      setManualInvoiceNumber("");
      setManualInvoiceDate("");
      if (isUsingGreenInvoice) {
        // Connected: ask auto vs manual first
        setShowManualInvoiceForm(false);
        setShowInvoiceChoice(true);
      } else {
        // Not connected: go straight to manual entry (optional number + date)
        setShowManualInvoiceForm(true);
        setShowInvoiceChoice(true);
      }
      return;
    }

    // For other columns, update status directly
    await updateStatus(item, targetColumn.targetStatus);
  };

  const updateStatus = async (item, newStatus, additionalData = {}) => {
    try {
      const oldStatus = item.status;

      // Grow: a card dragged to paid while a payment link is still active → ask first
      if (newStatus === TRANSACTION_STATUS.PAID && item.growLinkId) {
        const proceed = await growGuard.ensureNoActiveLink(item);
        if (!proceed) return;
      }
      
      await updateTransactionStatus({
        clinicId,
        transactionId: item.id,
        newStatus,
        additionalData,
        therapistId,
        therapistName,
        patientName: item.patientName,
        patientId: item.patientId,
      }).unwrap();

      // If moving between EXPECTED <-> PENDING, invalidate calendar tags
      // This updates the carousel on the dashboard
      const isExpectedPendingTransition =
        (oldStatus === TRANSACTION_STATUS.EXPECTED && newStatus === TRANSACTION_STATUS.PENDING) ||
        (oldStatus === TRANSACTION_STATUS.PENDING && newStatus === TRANSACTION_STATUS.EXPECTED);
      
      if (isExpectedPendingTransition && item.appointmentId) {
        dispatch(
          calendarApi.util.invalidateTags([
            { type: "Appointments", id: item.appointmentId },
            { type: "Appointments", id: "PENDING_ACTION" },
          ])
        );
      }
    } catch (error) {
      console.error("[CK-8840] Error updating transaction status:", error);
      showError("שגיאה בעדכון סטטוס התשלום. הכרטיס הוחזר למקומו.");
    }
  };

  const handleInvoiceChoice = async (choice) => {
    if (!pendingInvoiceItem) return;

    if (choice === "auto") {
      // Open Morning invoice modal via callback
      onInvoiceRequest?.(pendingInvoiceItem);
      closeInvoiceDialog();
    } else {
      // Manual: reveal optional invoice number + date entry
      setShowManualInvoiceForm(true);
    }
  };

  // Note: `invoiceNumber`/`invoiceDate` are the API contract — firebase.js
  // updateTransactionStatus maps them to doc fields `manualInvoiceNumber`/`manualInvoiceDate`.
  const handleManualInvoiceConfirm = async () => {
    if (!pendingInvoiceItem) return;
    const invoiceNumber = manualInvoiceNumber.trim();
    await updateStatus(pendingInvoiceItem, TRANSACTION_STATUS.INVOICE_MANUAL, {
      ...(invoiceNumber ? { invoiceNumber } : {}),
      ...(manualInvoiceDate ? { invoiceDate: manualInvoiceDate } : {}),
    });
    closeInvoiceDialog();
  };

  const closeInvoiceDialog = () => {
    setShowInvoiceChoice(false);
    setShowManualInvoiceForm(false);
    setPendingInvoiceItem(null);
    setManualInvoiceNumber("");
    setManualInvoiceDate("");
  };

  // Cancel expected forecast handlers
  const handleCancelForecastClick = (e, item) => {
    e.stopPropagation();
    setPendingCancelItem(item);
    setShowCancelForecast(true);
  };

  const handleConfirmCancelForecast = async () => {
    if (!pendingCancelItem) return;
    setIsCancelling(true);

    try {
      await forgiveDebt({
        clinicId,
        transactionId: pendingCancelItem.id,
        reason: "ביטול צפי הכנסה עתידי",
        therapistId,
        therapistName,
        patientName: pendingCancelItem.patientName,
        patientId: pendingCancelItem.patientId,
        amount: pendingCancelItem.amount,
      }).unwrap();

      setShowCancelForecast(false);
      setPendingCancelItem(null);
    } catch (error) {
      console.error("[CK-8845] Error cancelling expected forecast:", error);
      showError("שגיאה בביטול התחזית. נסו שוב.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDismissCancelForecast = () => {
    setShowCancelForecast(false);
    setPendingCancelItem(null);
  };

  // Format helpers
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    return `₪${parseFloat(amount).toLocaleString("he-IL")}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "numeric",
      month: "short",
    });
  };

  // Payment method helper
  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: "מזומן",
      credit: "אשראי",
      transfer: "העברה",
      bit: "אפליקציה",
      check: "צ׳ק",
      punch_card: "כרטיסיה",
      other: "אחר",
    };
    return labels[method] || "";
  };

  // Get status label for each column
  const getStatusLabel = (columnId, item) => {
    switch (columnId) {
      case "expected":
        return "צפי עתידי";
      case "pending":
        return "ממתין לתשלום";
      case "paid":
        return "שולם";
      case "invoiced":
        return item.status === TRANSACTION_STATUS.INVOICE_AUTO ? "חשבונית אוטו׳" : "חשבונית";
      default:
        return "";
    }
  };

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען נתונים..." />
    );
  }

  return (
    <div className="payment-kanban">
      <div className="kanban-columns">
        {KANBAN_COLUMNS.map((column) => {
          const items = getColumnItems(column.id);
          const ColumnIcon = column.icon;
          const isDragOver = dragOverColumn === column.id;
          const canDrop =
            draggedItem && draggedItem.sourceColumn !== column.id;

          return (
            <div
              key={column.id}
              className={`kanban-column ${column.color} ${isDragOver && canDrop ? "drag-over" : ""}`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column)}
            >
              {/* Column Header */}
              <div className="kanban-column-header">
                <div className="column-title">
                  <div className="column-title-icon">
                    <ColumnIcon size={16} weight="fill" />
                  </div>
                  <span>{column.title}</span>
                  {column.infoTooltip && (
                    <div 
                      className="column-info"
                      onMouseEnter={() => setActiveTooltip(column.id)}
                      onMouseLeave={() => setActiveTooltip(null)}
                    >
                      <Info size={14} weight="fill" />
                      {activeTooltip === column.id && (
                        <div className="column-info-tooltip">
                          {column.infoTooltip}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <span className="column-count">{items.length}</span>
              </div>

              {/* Column Content */}
              <div className="kanban-column-content">
                {items.length === 0 ? (
                  <div className="kanban-empty">
                    <span>אין רשומות</span>
                  </div>
                ) : (
                  items.map((item) => {
                    const isFromSplit = item.splitFromTransactionId;
                    const hasNoPatient = !item.patientId;
                    const paymentDate = item.actualPaymentDate || item.expectedPaymentDate;
                    const isManual = !item.appointmentId;
                    const statusLabel = getStatusLabel(column.id, item);

                    return (
                      <div
                        key={item.id}
                        className={`kanban-card ${hasNoPatient ? "no-patient" : ""} ${isFromSplit ? "from-split" : ""}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item, column.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onCardClick?.(item)}
                      >
                        {/* Patient Name + Cancel button for expected */}
                        <div className="card-name-row">
                          <span className={`card-name ${hasNoPatient ? "no-link" : ""}`}>
                            {item.patientName || item.description || "ללא שיוך"}
                          </span>
                          {column.id === "expected" && (
                            <button
                              className="card-cancel-forecast-btn"
                              onClick={(e) => handleCancelForecastClick(e, item)}
                              title="ביטול צפי"
                            >
                              <X size={12} weight="bold" />
                            </button>
                          )}
                        </div>

                        {/* Info Row - Status + All Tags on one line */}
                        <div className="card-info-row">
                          {/* Status Badge */}
                          <span className="card-status-badge">
                            {column.id === "expected" && <Hourglass size={10} weight="fill" />}
                            {column.id === "pending" && <Clock size={10} weight="fill" />}
                            {column.id === "paid" && <CheckCircle size={10} weight="fill" />}
                            {column.id === "invoiced" && <Receipt size={10} weight="fill" />}
                            {statusLabel}
                          </span>

                          {/* Amount Badge */}
                          <span className="card-amount-badge">
                            {formatCurrency(item.amount)}
                          </span>

                          {/* Date Badge */}
                          <span className="card-date-badge">
                            <CalendarBlank size={10} weight="fill" />
                            {formatDate(paymentDate)}
                          </span>

                          {/* Manual Invoice Number / Date Badge */}
                          {item.status === TRANSACTION_STATUS.INVOICE_MANUAL &&
                            (item.manualInvoiceNumber || item.manualInvoiceDate) && (
                              <span className="card-invoice-badge">
                                <Receipt size={10} weight="fill" />
                                {item.manualInvoiceNumber ? `#${item.manualInvoiceNumber}` : ""}
                                {item.manualInvoiceDate ? ` ${formatDate(item.manualInvoiceDate)}` : ""}
                              </span>
                            )}

                          {/* Payment Method Badge */}
                          {item.paymentMethod && (
                            <span className="card-method-badge">
                              {getPaymentMethodLabel(item.paymentMethod)}
                            </span>
                          )}

                          {/* Manual / Linked Indicator */}
                          {isManual ? (
                            <span className="card-source-badge manual">ידני</span>
                          ) : (
                            <span className="card-source-badge linked">מפגישה</span>
                          )}

                          {/* Split Badge */}
                          {isFromSplit && (
                            <span className="card-split-badge">
                              <ArrowsClockwise size={9} />
                            </span>
                          )}

                          {/* Grow payment link: chip (any state) or request button (unpaid, no active link) */}
                          {(() => {
                            const growLink = growLinks.linkFor(item);
                            if (growLink) {
                              return (
                                <GrowLinkStatusChip
                                  link={growLink}
                                  size="xs"
                                  onClick={() => growLinks.openLink(growLink)}
                                />
                              );
                            }
                            if (column.id === "expected" || column.id === "pending") {
                              return <RequestPaymentButton transactions={[item]} size="sm" variant="ghost" />;
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Drop zone indicator */}
              {isDragOver && canDrop && (
                <div className="kanban-drop-indicator">
                  <span>שחרר כאן</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Invoice Choice Dialog */}
      {showInvoiceChoice && (
        <div className="invoice-choice-overlay" onClick={closeInvoiceDialog}>
          <div className="invoice-choice-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="invoice-choice-header">
              <Receipt size={24} weight="duotone" />
              <h3>{showManualInvoiceForm ? "סימון חשבונית ידנית" : "הפקת חשבונית"}</h3>
            </div>

            {showManualInvoiceForm ? (
              <>
                <p>פרטי החשבונית עבור {pendingInvoiceItem?.patientName || "התשלום"} (לא חובה)</p>
                <div className="invoice-manual-form">
                  <label className="invoice-manual-field">
                    <span>מספר חשבונית</span>
                    <input
                      type="text"
                      className="invoice-manual-input"
                      placeholder="לדוגמה: 1042"
                      value={manualInvoiceNumber}
                      onChange={(e) => setManualInvoiceNumber(e.target.value)}
                    />
                  </label>
                  <label className="invoice-manual-field">
                    <span>תאריך הפקת החשבונית</span>
                    <DatePicker
                      value={manualInvoiceDate}
                      onChange={(e) => setManualInvoiceDate(e.target.value)}
                    />
                  </label>
                </div>
                <button className="ckmi-manual-save-btn" onClick={handleManualInvoiceConfirm}>
                  <CheckCircle size={18} weight="fill" />
                  <span>שמירה</span>
                </button>
                <button className="invoice-choice-cancel" onClick={closeInvoiceDialog}>
                  ביטול
                </button>
              </>
            ) : (
              <>
                <p>
                  איך תרצה להנפיק חשבונית עבור {pendingInvoiceItem?.patientName || "התשלום"}?
                </p>
                <div className="invoice-choice-options">
                  <button
                    className="invoice-option auto"
                    onClick={() => handleInvoiceChoice("auto")}
                  >
                    <Receipt size={20} weight="fill" />
                    <span>הפקה אוטומטית</span>
                    <small>דרך מורנינג</small>
                  </button>
                  <button
                    className="invoice-option manual"
                    onClick={() => handleInvoiceChoice("manual")}
                  >
                    <CheckCircle size={20} weight="fill" />
                    <span>סימון ידני</span>
                    <small>הנפקתי בעצמי</small>
                  </button>
                </div>
                <button
                  className="invoice-choice-cancel"
                  onClick={closeInvoiceDialog}
                >
                  ביטול
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cancel Expected Forecast Dialog */}
      {showCancelForecast && pendingCancelItem && (
        <div className="cancel-forecast-overlay" onClick={handleDismissCancelForecast}>
          <div className="cancel-forecast-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="cancel-forecast-header">
              <Warning size={24} weight="duotone" />
              <h3>ביטול צפי הכנסה עתידי</h3>
            </div>

            <div className="cancel-forecast-patient">
              <span>{pendingCancelItem.patientName || pendingCancelItem.description || "ללא שיוך"}</span>
              <span className="cancel-forecast-amount">
                ₪{parseFloat(pendingCancelItem.amount).toLocaleString("he-IL")}
              </span>
            </div>

            {pendingCancelItem.appointmentId ? (
              <div className="cancel-forecast-message">
                <div className="cancel-forecast-notice">
                  <CalendarCheck size={18} weight="duotone" />
                  <span>הפגישה עדיין קיימת ביומן</span>
                </div>
                <p>
                  ביטול הצפי הכספי <strong>לא יבטל את הפגישה עצמה</strong>.
                  הפגישה תמשיך להופיע ביומן כרגיל.
                </p>
                <p>
                  לאחר שהפגישה תסתיים, תוכל/י עדיין לתעד את התשלום —
                  להעביר ל<strong>״ממתין לגבייה״</strong> או לסמן כ<strong>״שולם״</strong> מתוך עמוד הפגישה או דף הפיננסים.
                </p>
              </div>
            ) : (
              <div className="cancel-forecast-message">
                <p>
                  פעולה זו תבטל את רשומת הצפי הכספי.
                  לא ניתן לבטל פעולה זו.
                </p>
              </div>
            )}

            <div className="cancel-forecast-actions">
              <button
                className="cancel-forecast-btn secondary"
                onClick={handleDismissCancelForecast}
                disabled={isCancelling}
              >
                חזרה
              </button>
              <button
                className="cancel-forecast-btn danger"
                onClick={handleConfirmCancelForecast}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <LoaderLoop size="xs" />
                ) : (
                  "אשר ביטול צפי"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Updating indicator */}
      {isUpdating && (
        <div className="kanban-updating">
          <LoaderLoop size="xs" />
          <span>מעדכן...</span>
        </div>
      )}
      {growLinks.popover}
      {growGuard.prompt}
    </div>
  );
}

export default PaymentKanban;
